import React, { useState, useRef, useEffect } from "react";

interface EditableTextProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableText: React.FC<EditableTextProps> = ({
  text,
  onEdit,
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(text);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(text);
  }, [text]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onEdit(newValue);
  };

  const closeInput = (shouldRevert: boolean) => {
    setIsEditing(false);
    if (shouldRevert) {
      setValue(text);
      onEdit(text);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Escape") {
      e.preventDefault();
      closeInput(e.key === "Escape");
    }
  };

  const handleBlur = () => {
    closeInput(false);
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={`bg-transparent border-b-2 border-white/30 focus:border-white/70 outline-none px-2 py-1 w-full ${className}`}
        style={{
          ...style,
          caretColor: "white",
          cursor: "text",
          transition: "all 0.2s ease",
        }}
        placeholder={placeholder}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <div
      onClick={handleClick}
      className={`cursor-text ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
    >
      {text || <span className="opacity-50">{placeholder}</span>}
    </div>
  );
};

interface SlideContent {
  heading?: string;
  bodyContent?:
    | string
    | Array<{
        point: string;
        icon: string;
        detail: string;
      }>;
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultContent = [
  {
    point: "Light Absorption Process",
    icon: "☀️",
    detail:
      "Chlorophyll molecules in thylakoid membranes capture photons of sunlight",
  },
  {
    point: "Water Splitting (Photolysis)",
    icon: "💧",
    detail: "H2O molecules are split into hydrogen ions, electrons, and oxygen",
  },
  {
    point: "Electron Transport Chain",
    icon: "⚡",
    detail:
      "Electrons flow through photosystems I and II, creating energy gradient",
  },
  {
    point: "ATP Synthesis",
    icon: "🔋",
    detail: "Chemical energy is stored in ATP through chemiosmosis process",
  },
  {
    point: "NADPH Formation",
    icon: "🧬",
    detail: "Electrons and H+ combine with NADP+ to form NADPH",
  },
  {
    point: "Carbon Fixation",
    icon: "🌱",
    detail: "CO2 is converted to glucose through Calvin cycle reactions",
  },
  {
    point: "Oxygen Release",
    icon: "🌬️",
    detail: "O2 is released as a byproduct of water splitting reaction",
  },
  {
    point: "Glucose Production",
    icon: "🍯",
    detail: "Sugar molecules are synthesized using ATP and NADPH energy",
  },
  {
    point: "Energy Storage",
    icon: "📦",
    detail: "Glucose is stored as starch or converted to other molecules",
  },
  {
    point: "Cellular Distribution",
    icon: "🔄",
    detail: "Energy-rich molecules are distributed throughout the plant",
  },
];

const MainBodySlide2 = {
  id: "main-body-slide2",
  name: "Main Body Slide (Horizontal Flow)",
  fields: [
    {
      id: "heading",
      label: "Body Heading",
      type: "text",
      defaultValue: "Core Concepts of Photosynthesis",
    },
    {
      id: "bodyContent",
      label: "Body Content (JSON array of {point, icon, detail})",
      type: "text",
      defaultValue: JSON.stringify(defaultContent.slice(0, 8)),
    },
    {
      id: "teacherNote",
      label: "Teacher Note",
      type: "text",
      defaultValue: "",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue:
        "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      heading = "Core Concepts of Photosynthesis",
      bodyContent = JSON.stringify(defaultContent.slice(0, 8)),
      teacherNote = "",
      bgColor = "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
      onEdit = () => {},
    } = content;

    let parsedBodyContent = defaultContent.slice(0, 8);
    try {
      if (typeof bodyContent === "string") {
        const parsed = JSON.parse(bodyContent);
        if (Array.isArray(parsed)) {
          parsedBodyContent = parsed
            .map((item) => ({
              point: item.point || "",
              icon: item.icon || "📌",
              detail: item.detail || "",
            }))
            .slice(0, 8);
        }
      } else if (Array.isArray(bodyContent)) {
        parsedBodyContent = bodyContent
          .map((item) => ({
            point: item.point || "",
            icon: item.icon || "📌",
            detail: item.detail || "",
          }))
          .slice(0, 8);
      }
    } catch (error) {
      console.warn("Error parsing body content:", error);
      console.warn("Raw body content:", bodyContent);
    }

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    const handleBodyContentEdit = (
      index: number,
      field: string,
      value: string,
    ) => {
      try {
        const currentContent = [...parsedBodyContent];
        currentContent[index] = {
          ...currentContent[index],
          [field]: value,
        };

        const newContent = {
          ...content,
          bodyContent: JSON.stringify(currentContent),
        };
        onEdit(newContent);

        if (window.updateLiveConfig) {
          window.updateLiveConfig(newContent);
        }
      } catch (error) {
        console.error("Error updating body content:", error);
      }
    };

    const getGridLayout = (itemCount: number) => {
      switch (itemCount) {
        case 1:
          return "grid-cols-1";
        case 2:
          return "grid-cols-2";
        case 3:
          return "grid-cols-3";
        case 4:
          return "grid-cols-2";
        case 5:
          return "grid-cols-3";
        case 6:
          return "grid-cols-3";
        case 7:
          return "grid-cols-3";
        case 8:
          return "grid-cols-3";
        default:
          return "grid-cols-3";
      }
    };

    const getCardHeight = (itemCount: number) => {
      switch (itemCount) {
        case 1:
          return "h-[calc(100vh-16rem)]";
        case 2:
          return "h-[calc(100vh-16rem)]";
        case 3:
          return "h-[calc(100vh-16rem)]";
        case 4:
          return "h-[calc((100vh-16rem)/2)]";
        case 5:
        case 6:
          return "h-[calc((100vh-16rem)/2)]";
        case 7:
        case 8:
        case 9:
          return "h-[calc((100vh-16rem)/3)]";
        default:
          return "h-[calc((100vh-16rem)/3)]";
      }
    };

    const getTextSizes = (itemCount: number) => {
      switch (itemCount) {
        case 1:
        case 2:
          return {
            point: "text-2xl",
            detail: "text-lg",
            icon: "text-4xl",
          };
        case 3:
        case 4:
          return {
            point: "text-xl",
            detail: "text-base",
            icon: "text-3xl",
          };
        case 5:
        case 6:
          return {
            point: "text-lg",
            detail: "text-sm",
            icon: "text-2xl",
          };
        default:
          return {
            point: "text-base",
            detail: "text-xs",
            icon: "text-xl",
          };
      }
    };

    const gridClass = getGridLayout(parsedBodyContent.length);
    const textSizes = getTextSizes(parsedBodyContent.length);
    const cardHeight = getCardHeight(parsedBodyContent.length);

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div
            className="absolute top-0 right-0 w-[800px] h-[800px] bg-slate-500/5 rounded-full 
                      transform translate-x-1/3 -translate-y-1/2 animate-pulse"
          />
          <div
            className="absolute -bottom-20 -left-20 w-[600px] h-[600px] bg-slate-500/5 rounded-full 
                      animate-pulse"
            style={{ animationDelay: "1s" }}
          />
          <div
            className="absolute top-1/2 left-1/4 w-20 h-20 bg-slate-500/10 rounded-full 
                      animate-pulse"
            style={{ animationDelay: "1.5s" }}
          />
        </div>

        {/* Main Content Container */}
        <div className="relative z-10 flex flex-col h-full p-6">
          {/* Header Section */}
          <div className="text-center mb-4">
            <h2 className="text-4xl font-bold mb-2 text-slate-100">
              {heading}
            </h2>
            <div className="w-32 h-1 mx-auto bg-gradient-to-r from-slate-300/50 to-transparent" />
          </div>

          {/* Cards Grid */}
          <div
            className={`flex-1 flex flex-wrap gap-4 mb-4 justify-center items-start content-start`}
          >
            {parsedBodyContent.map((item, idx) => (
              <div
                key={idx}
                className={`group bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-slate-500/20
                          transform transition-all duration-300 hover:bg-white/10 hover:border-slate-400/40 
                          flex flex-col w-[calc(33%-1rem)] min-h-[200px] relative overflow-hidden`}
              >
                {/* Card Background Pattern */}
                <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-400/20 to-transparent" />
                </div>

                {/* Card Number */}
                <div
                  className="absolute top-0 left-0 w-8 h-8 bg-slate-600/90 rounded-tl-xl rounded-br-xl
                            flex items-center justify-center text-lg font-bold
                            transform transition-all duration-300 group-hover:bg-slate-500"
                >
                  {idx + 1}
                </div>

                <div className="flex items-start space-x-3 pt-8 mb-2">
                  <span
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleBodyContentEdit(
                        idx,
                        "icon",
                        e.currentTarget.textContent || "",
                      )
                    }
                    className={`${textSizes.icon} cursor-text shrink-0 group-hover:scale-110 transition-transform`}
                  >
                    {item.icon}
                  </span>
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) =>
                      handleBodyContentEdit(
                        idx,
                        "point",
                        e.currentTarget.textContent || "",
                      )
                    }
                    className={`${textSizes.point} font-bold text-slate-100 cursor-text leading-tight flex-1`}
                  >
                    {item.point}
                  </div>
                </div>

                <div
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={(e) =>
                    handleBodyContentEdit(
                      idx,
                      "detail",
                      e.currentTarget.textContent || "",
                    )
                  }
                  className={`${textSizes.detail} text-slate-100/90 leading-snug cursor-text flex-1 pl-11`}
                >
                  {item.detail}
                </div>

                {/* Flow Connector */}
                {idx < parsedBodyContent.length - 1 && (
                  <div className="absolute -right-6 top-1/2 transform -translate-y-1/2 w-12 h-0.5 bg-slate-500/20" />
                )}

                {/* Corner Decorations */}
                <div className="absolute bottom-0 right-0 w-8 h-8">
                  <div className="absolute bottom-0 right-0 w-full h-0.5 bg-gradient-to-l from-slate-300/50" />
                  <div className="absolute bottom-0 right-0 w-0.5 h-full bg-gradient-to-t from-slate-300/50" />
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Section */}
          <div className="relative">
            {/* Teacher Note */}
            {teacherNote && (
              <div className="relative mb-2">
                <div
                  className="absolute -left-4 top-1/2 transform -translate-y-1/2 w-3 h-3 bg-yellow-400 
                            rounded-full animate-ping"
                  style={{ animationDuration: "3s" }}
                />
                <div className="bg-slate-900/30 backdrop-blur-sm rounded-2xl px-6 py-2 max-w-3xl mx-auto border border-slate-500/10">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-slate-400/80">📝</span>
                    <EditableText
                      text={teacherNote}
                      onEdit={handleEdit("teacherNote")}
                      className="text-sm text-center font-light text-slate-100/90"
                      placeholder="Enter teacher note..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Progress Dots */}
            <div className="flex justify-center space-x-2 mt-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-slate-300/50 animate-pulse"
                  style={{ animationDelay: `${i * 0.5}s` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  },
};

export default MainBodySlide2;
